import React, { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { hotjar } from 'react-hotjar';

// Components
import Layout from './layouts/Layout';
import AuthError from './pages/Login/AuthError';

// Utils
import { initializeAuth, initializeUserData, setApiToken, clearAuth } from './redux/user/authSlice';
import { initializeAppData } from './redux/app/appSlice';
import { setInvestigateTokenGetter } from './utils/API/investigateAuth';
import useSignoutSync from './hooks/useSignoutSync';

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user, getAccessTokenSilently, isLoading, error } = useAuth0();
  useSignoutSync();

  // app-api: httpClientInvestigate asks Auth0 for a fresh token on every request. Requests sent
  // before this runs wait for it; when nobody is signed in they go out without a token (401).
  useEffect(() => {
    if (isLoading) return;
    setInvestigateTokenGetter(isAuthenticated ? getAccessTokenSilently : null);
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  const initializeAuthentication = useCallback(async () => {
    try {
      // Always a fresh token after sign-in: the stored one may have expired.
      const token = await getAccessTokenSilently();
      dispatch(setApiToken(token));
    } catch (error) {
      console.error(error);
      dispatch(clearAuth());
    }
  }, [dispatch, getAccessTokenSilently]);

  const initializeApplication = useCallback(async () => {
    if (isAuthenticated && user) {
      await initializeAuthentication();
      await dispatch(initializeUserData(user)).unwrap();
      await dispatch(initializeAppData()).unwrap();
    }
  }, [isAuthenticated, user, initializeAuthentication, dispatch]);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoading) {
      if (isAuthenticated) {
        initializeApplication();
      } else {
        dispatch(clearAuth());
      }
    }
  }, [isLoading, isAuthenticated, initializeApplication, dispatch]);

  useEffect(() => {
    hotjar.initialize(3845779, 6);
  }, []);

  if (error) {
    return <AuthError error={error} />;
  }

  return (
    <Router>
      <Layout />
    </Router>
  );
}

export default App;
